/**
 * Keeps the floating support-chat launcher (public/widgets/support-widget.js,
 * fixed to the bottom-right corner of the TOP window) clear of a composer that
 * is pinned to the bottom of the viewport.
 *
 * While a composer occupies the launcher's corner, this lifts the launcher just
 * above it by setting `--tsw-launcher-lift` on the top window's <html>; the
 * widget's CSS adds that to its `bottom`. Works from inside same-origin iframes
 * (main-window iframe tabs, /popout/app tabs), where the widget is not loaded
 * and the launcher lives in the parent window. Several composers can register
 * at once (thread + thread panel, hidden tabs); the largest lift wins, and the
 * variable is removed when the last one goes away.
 */
import { useEffect, type RefObject } from 'react';

export const SUPPORT_BUBBLE_LIFT_VAR = '--tsw-launcher-lift';
/** The launcher's own inset from the viewport edge, and its size (support-widget.js). */
const LAUNCHER_INSET_PX = 20;
const LAUNCHER_INSET_PHONE_PX = 15; // support-widget.js @media (max-width: 480px)
const LAUNCHER_SIZE_PX = 60;
/** Gap between the lifted launcher and the top of the composer. */
const GAP_PX = 12;
/** Safety net: re-measure this often in case the composer moved in a way no observer saw. */
const RECHECK_MS = 1000;

type Registry = Map<object, number>;
const REGISTRY_KEY = '__weaveinSupportBubbleLifts';

/** The top-most same-origin window (where the launcher lives). */
function launcherWindow(): Window {
  let w: Window = window;
  try {
    while (w.parent && w.parent !== w && w.frameElement) w = w.parent;
  } catch {
    /* cross-origin parent: stop here */
  }
  return w;
}

function registryOf(w: Window): Registry {
  const host = w as unknown as Record<string, Registry | undefined>;
  if (!host[REGISTRY_KEY]) host[REGISTRY_KEY] = new Map();
  return host[REGISTRY_KEY] as Registry;
}

function apply(w: Window): void {
  const lifts = [...registryOf(w).values()];
  const lift = lifts.length ? Math.max(0, ...lifts) : 0;
  const style = w.document.documentElement.style;
  const next = lift > 0 ? `${Math.round(lift)}px` : '';
  if (style.getPropertyValue(SUPPORT_BUBBLE_LIFT_VAR) === next) return; // re-checks run often
  if (next) style.setProperty(SUPPORT_BUBBLE_LIFT_VAR, next);
  else style.removeProperty(SUPPORT_BUBBLE_LIFT_VAR);
}

/**
 * Where the launcher sits when NOT lifted, in `top`'s viewport. Measured from the
 * live `.tsw-launcher` element when the widget has rendered (its real size and
 * right inset), else the widget's defaults. Returns null when the user dragged it
 * to a custom spot: the lift variable no longer moves it (inline top/left win).
 */
export function launcherFootprint(top: Window): { left: number; right: number; top: number; bottom: number } | null {
  const W = top.innerWidth;
  const H = top.innerHeight;
  const bottomInset = W <= 480 ? LAUNCHER_INSET_PHONE_PX : LAUNCHER_INSET_PX;
  let size = LAUNCHER_SIZE_PX;
  let rightInset = bottomInset;
  let leftInset: number | null = null; // set when the widget is configured bottom-left
  try {
    const el = top.document.querySelector('.tsw-launcher') as HTMLElement | null;
    if (el) {
      if (el.style.top || el.style.left) return null; // dragged: positioned by inline top/left
      // offsetWidth ignores the hover scale transform; computed `right` is not animated.
      if (el.offsetWidth > 0) size = Math.max(el.offsetWidth, el.offsetHeight);
      const cs = top.getComputedStyle(el);
      const right = parseFloat(cs.right);
      const left = parseFloat(cs.left);
      if (cs.right === 'auto' && Number.isFinite(left)) leftInset = left;
      else if (Number.isFinite(right)) rightInset = right;
    }
  } catch {
    /* fall back to defaults */
  }
  const x = leftInset != null ? leftInset : W - rightInset - size;
  return { left: x, right: x + size, top: H - bottomInset - size, bottom: H - bottomInset };
}

/** How far the launcher must rise so it no longer covers `el` (0 when it doesn't). */
export function liftNeededFor(el: Element, top: Window): number {
  const r = el.getBoundingClientRect();
  if (!r.width || !r.height) return 0; // hidden (inactive tab, collapsed pane)
  let elTop = r.top;
  let elBottom = r.bottom;
  let elLeft = r.left;
  let elRight = r.right;
  // Translate into the launcher window's viewport through any iframes.
  let w: Window = el.ownerDocument.defaultView || window;
  try {
    while (w !== top) {
      const frame = w.frameElement;
      if (!frame) return 0;
      const fr = frame.getBoundingClientRect();
      elTop += fr.top;
      elBottom += fr.top;
      elLeft += fr.left;
      elRight += fr.left;
      w = w.parent;
    }
  } catch {
    return 0;
  }
  const H = top.innerHeight;
  const spot = launcherFootprint(top);
  if (!spot) return 0;
  // Any intersection between the composer and the (un-lifted) launcher needs a lift.
  const overlaps = elLeft < spot.right && elRight > spot.left && elTop < spot.bottom && elBottom > spot.top;
  if (!overlaps) return 0;
  const lift = spot.bottom - elTop + GAP_PX;
  // A composer taller than half the screen is not "pinned to the bottom"; leave the launcher be.
  return lift > 0 && lift < H / 2 ? lift : 0;
}

/** Lift the support launcher above `ref`'s element while it is mounted and visible. */
export function useSupportBubbleClearance(ref: RefObject<HTMLElement | null>, active = true): void {
  useEffect(() => {
    const el = ref.current;
    if (!active || !el || typeof window === 'undefined') return;
    const top = launcherWindow();
    const key = {};
    const update = () => {
      registryOf(top).set(key, liftNeededFor(el, top));
      apply(top);
    };
    let frame = 0;
    const schedule = () => {
      if (frame || typeof requestAnimationFrame !== 'function') return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update();
      });
    };
    update();
    schedule();
    // The composer can MOVE without changing size (a banner above or below it
    // appears, the pane is resized, the page scrolls). Watching only the composer
    // left the launcher where it was — over the Send button. Watch every ancestor
    // too, plus scrolls, and re-check periodically as a safety net.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    for (let n: Element | null = el; n; n = n.parentElement) observer?.observe(n);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    if (top !== window) top.addEventListener('resize', schedule);
    const timer = setInterval(update, RECHECK_MS);
    return () => {
      if (frame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
      clearInterval(timer);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, { capture: true });
      if (top !== window) top.removeEventListener('resize', schedule);
      registryOf(top).delete(key);
      apply(top);
    };
  }, [ref, active]);
}
